import {
  CLAVES_DE_ACCESOS, escribirAccesos, leerAccesos, type AccesoGuardado,
} from '@/lib/accesos';
import { destinoPara, type Destino } from './navegacion';

/**
 * accesos.ts — quién ESCRIBE los favoritos y el historial.
 *
 * El contrato —nombre del evento, forma del registro, claves— vive en
 * `lib/accesos.ts`, que es de donde lo lee la tarjeta «Tus accesos» del Inicio.
 * Aquí solo se escribe.
 *
 * ## El nombre del destino sale del DATO, no del menú
 *
 * `trackRecent()` leía la etiqueta y el icono del propio menú:
 *
 * ```js
 * const el = exacto || document.querySelector(`.sidebar [data-page="${page}"]`);
 * if (!el) return;                                   // ← aquí acababa todo
 * const label = el.querySelector('span')?.textContent?.trim();
 * ```
 *
 * Al pasar el menú a React ese selector no encuentra nada, y ese `return`
 * temprano habría hecho que **«Recientes» dejara de registrar para siempre**: sin
 * error, sin consola, con la tarjeta de Inicio enseñando un historial congelado
 * el día del despliegue. Ahora la etiqueta y el icono salen de `navegacion.ts`,
 * que es donde se declaran.
 */

export const FAV_MAX = 8;
const RECIENTES_MAX = 10;

/** La forma en que se guarda un destino. */
function comoAcceso(d: Destino, grupo: string): AccesoGuardado {
  return { href: `#${d.hash}`, label: d.etiqueta, iconClass: `bi ${d.icono} nav-icon`, group: grupo };
}

export function favoritos(): AccesoGuardado[] {
  return leerAccesos(CLAVES_DE_ACCESOS.favoritos);
}

/**
 * Fija o quita un favorito. Devuelve la lista nueva, o `null` si no cabía.
 *
 * El tope son ocho y lo dice: la versión anterior también avisaba, y callarlo
 * convertiría el noveno clic en un botón que no hace nada.
 */
export function alternarFavorito(d: Destino, grupo: string): AccesoGuardado[] | null {
  const href = `#${d.hash}`;
  const actuales = favoritos();
  if (actuales.some((f) => f.href === href)) {
    const nuevos = actuales.filter((f) => f.href !== href);
    escribirAccesos(CLAVES_DE_ACCESOS.favoritos, nuevos);
    return nuevos;
  }
  if (actuales.length >= FAV_MAX) {
    Utils.toast(`Máximo ${FAV_MAX} favoritos — quita alguno primero`, 'error');
    return null;
  }
  const nuevos = [...actuales, comoAcceso(d, grupo)];
  escribirAccesos(CLAVES_DE_ACCESOS.favoritos, nuevos);
  return nuevos;
}

/**
 * Apunta una visita en el historial.
 *
 * Un destino que no está en el menú no se apunta —igual que antes, cuando el
 * `querySelector` no encontraba fila—: `#formulario`, `#reset` y los detalles no
 * son sitios a los que volver desde una lista. Los detalles son
 * `#course-detail/<id>`, `#galeria-evento/<id>` y los suyos: páginas propias del
 * `PAGE_MAP` que no tienen entrada en el menú.
 *
 * **`#cursos/5` no es uno de ellos**, aunque lo parezca: `cursos` sí está en el
 * menú, `Cursos.tsx` no lee `window._routeParams` —el único que lo hace en
 * Formación es `DetalleDeCurso.tsx`—, así que esa dirección abre el catálogo
 * entero y tira el `5`. Y aquí se apunta, por el camino de `destinoPara`: cae al
 * `page` y se guarda como «Cursos» apuntando a `#cursos`, que es lo correcto y
 * es el mismo caso que `#perfil/ficha`.
 */
export function registrarVisita(page: string, hash: string): void {
  const d = destinoPara(page, hash);
  if (!d) return;
  // El hash EXACTO solo si es el del destino; si la sub-ruta no es suya, la
  // ruta base. Ver `destinoPara`.
  const acceso = comoAcceso(d, d.grupo);
  const previos = leerAccesos(CLAVES_DE_ACCESOS.recientes).filter((r) => r.href !== acceso.href);
  escribirAccesos(CLAVES_DE_ACCESOS.recientes, [acceso, ...previos].slice(0, RECIENTES_MAX));
}
